import type {
  Cargo,
  InstallationType,
  Level1To10,
  MaterialId,
  Ship,
  ShipType,
} from "../shared/game-types";
import { NAVIGATION_CRUISE_SPEED_C } from "./balance";

export interface EnergyLevelDefinition {
  level: Level1To10;
  name: string;
  use: string;
}

export const ENERGY_LEVELS: readonly EnergyLevelDefinition[] = [
  { level: 1, name: "化学燃料", use: "起降、初级基地、近距离运输" },
  { level: 2, name: "铀-235 裂变与核热", use: "稳定基地电力、核热推进" },
  { level: 3, name: "裂变电力与电推进", use: "高效率长期无人任务" },
  { level: 4, name: "高温裂变和气态堆芯", use: "高性能行星际推进" },
  { level: 5, name: "裂变脉冲", use: "重型深空飞船" },
  { level: 6, name: "氘氚聚变", use: "聚变能源和初级星际飞船" },
  { level: 7, name: "氘氦-3 聚变", use: "低中子高级聚变" },
  { level: 8, name: "反质子催化聚变", use: "相对论前期推进" },
  { level: 9, name: "物质-反物质湮灭", use: "极高能量密度推进" },
  { level: 10, name: "外部激光束/光子推进", use: "极端光帆和恒星级工程" },
];

export interface MaterialLevelDefinition {
  level: Level1To10;
  name: string;
  use: string;
}

export const MATERIAL_LEVELS: readonly MaterialLevelDefinition[] = [
  { level: 1, name: "钢、铝、铜、普通合金", use: "基础基地与化学火箭" },
  { level: 2, name: "镍基高温合金、GRCop", use: "发动机与热交换结构" },
  { level: 3, name: "钼、碳-碳复合材料", use: "离子推进器与耐烧蚀结构" },
  { level: 4, name: "钨铼、碳化铪陶瓷", use: "极端高温裂变设备" },
  { level: 5, name: "高强钢、先进复合层", use: "核脉冲与重型船体" },
  { level: 6, name: "Nb3Sn 超导体、钨", use: "氘氚聚变系统" },
  { level: 7, name: "REBCO、碳化硅复材", use: "高级聚变与高速飞船" },
  { level: 8, name: "高等级超导与耐辐射材料", use: "反质子催化系统" },
  { level: 9, name: "反物质容器材料、难熔合金", use: "反物质储存与推进" },
  { level: 10, name: "氮化钛薄膜、碳纳米管复合膜", use: "光帆与光子系统" },
];

export interface Cost {
  energy: number;
  materials: Partial<Record<MaterialId, number>>;
}

export function developmentUpgradeCost(targetLevel: number): Cost {
  return {
    energy: 45 * targetLevel ** 2,
    materials: {
      structural: 24 * targetLevel ** 2,
      highTemp: 5 * targetLevel ** 2,
      ...(targetLevel >= 4 ? { nuclear: 3 * targetLevel ** 2 } : {}),
    },
  };
}

export interface InstallationDefinition {
  id: InstallationType;
  name: string;
  description: string;
  minimumDevelopment: number;
  requiredIndustry: Level1To10;
  requiredEnergy: Level1To10;
  requiredMaterials: Level1To10;
  cost: Cost;
  buildYears: number;
  energyProduction: number;
  energyMaintenance: number;
  researchProduction: number;
  materialProduction: number;
}

export const INSTALLATIONS: Readonly<Record<InstallationType, InstallationDefinition>> = {
  probeStation: {
    id: "probeStation", name: "自动探测站", description: "持续观测、更新资源模型", minimumDevelopment: 2,
    requiredIndustry: 1, requiredEnergy: 1, requiredMaterials: 1, cost: { energy: 35, materials: { structural: 18 } },
    buildYears: 1, energyProduction: 0, energyMaintenance: 1, researchProduction: 5, materialProduction: 0,
  },
  miningOutpost: {
    id: "miningOutpost", name: "无人采矿站", description: "开采矿物、冰与挥发物", minimumDevelopment: 3,
    requiredIndustry: 2, requiredEnergy: 2, requiredMaterials: 2, cost: { energy: 85, materials: { structural: 55, highTemp: 8 } },
    buildYears: 4, energyProduction: 0, energyMaintenance: 5, researchProduction: 0, materialProduction: 14,
  },
  atmosphericCollector: {
    id: "atmosphericCollector", name: "大气采集站", description: "提取氢、氦与推进剂", minimumDevelopment: 3,
    requiredIndustry: 2, requiredEnergy: 2, requiredMaterials: 2, cost: { energy: 110, materials: { structural: 45, highTemp: 16 } },
    buildYears: 6, energyProduction: 16, energyMaintenance: 6, researchProduction: 0, materialProduction: 2,
  },
  orbitalProcessor: {
    id: "orbitalProcessor", name: "轨道加工站", description: "真空冶金、燃料提纯与材料加工", minimumDevelopment: 3,
    requiredIndustry: 3, requiredEnergy: 3, requiredMaterials: 3, cost: { energy: 160, materials: { structural: 80, highTemp: 25, nuclear: 10 } },
    buildYears: 12, energyProduction: 8, energyMaintenance: 10, researchProduction: 2, materialProduction: 18,
  },
  researchBase: {
    id: "researchBase", name: "科研基地", description: "特殊环境研究和长期观测", minimumDevelopment: 3,
    requiredIndustry: 2, requiredEnergy: 2, requiredMaterials: 2, cost: { energy: 125, materials: { structural: 42, highTemp: 12 } },
    buildYears: 8, energyProduction: 0, energyMaintenance: 8, researchProduction: 22, materialProduction: 0,
  },
  industrialBase: {
    id: "industrialBase", name: "工业基地", description: "制造设备、船体与补给品", minimumDevelopment: 4,
    requiredIndustry: 4, requiredEnergy: 4, requiredMaterials: 4, cost: { energy: 300, materials: { structural: 160, highTemp: 65, nuclear: 25 } },
    buildYears: 24, energyProduction: 48, energyMaintenance: 18, researchProduction: 6, materialProduction: 40,
  },
  colony: {
    id: "colony", name: "殖民地", description: "人口、科研、行政与综合工业", minimumDevelopment: 5,
    requiredIndustry: 4, requiredEnergy: 4, requiredMaterials: 4, cost: { energy: 500, materials: { structural: 250, highTemp: 90, nuclear: 35, superconductor: 15 } },
    buildYears: 48, energyProduction: 70, energyMaintenance: 30, researchProduction: 35, materialProduction: 22,
  },
};

export interface ShipDefinition {
  id: ShipType;
  name: string;
  description: string;
  requiredIndustry: Level1To10;
  requiredNavigation: Level1To10;
  requiredMilitary: Level1To10;
  cost: Cost;
  buildYears: number;
  hull: number;
  propulsion: number;
  sensors: number;
  weapons: number;
  cargoCapacity: number;
  fuel: number;
  speedMultiplier: number;
}

export const SHIP_TYPES: Readonly<Record<ShipType, ShipDefinition>> = {
  probe: {
    id: "probe", name: "自动探测器", description: "低成本轨道勘测与信号观测", requiredIndustry: 1, requiredNavigation: 1, requiredMilitary: 1,
    cost: { energy: 30, materials: { structural: 12 } }, buildYears: 2, hull: 100, propulsion: 60, sensors: 85, weapons: 0, cargoCapacity: 2, fuel: 45, speedMultiplier: 1,
  },
  transport: {
    id: "transport", name: "运输船", description: "在工业节点间运送能源与材料", requiredIndustry: 2, requiredNavigation: 1, requiredMilitary: 1,
    cost: { energy: 90, materials: { structural: 50, highTemp: 8 } }, buildYears: 6, hull: 100, propulsion: 55, sensors: 35, weapons: 5, cargoCapacity: 180, fuel: 90, speedMultiplier: 1,
  },
  heavyTransport: {
    id: "heavyTransport", name: "重型运输船", description: "一次装载普通运输船 5 倍货物，以三分之一航速执行低频大宗补给", requiredIndustry: 5, requiredNavigation: 5, requiredMilitary: 1,
    cost: { energy: 360, materials: { structural: 260, highTemp: 70, nuclear: 25 } }, buildYears: 20, hull: 100, propulsion: 55, sensors: 35, weapons: 5, cargoCapacity: 900, fuel: 240, speedMultiplier: 1 / 3,
  },
  colony: {
    id: "colony", name: "殖民船", description: "建立首个长期有人据点", requiredIndustry: 4, requiredNavigation: 3, requiredMilitary: 1,
    cost: { energy: 380, materials: { structural: 190, highTemp: 70, nuclear: 25 } }, buildYears: 24, hull: 100, propulsion: 50, sensors: 45, weapons: 5, cargoCapacity: 400, fuel: 160, speedMultiplier: 1,
  },
  patrol: {
    id: "patrol", name: "巡逻舰", description: "护航、驱逐与有限军事任务", requiredIndustry: 2, requiredNavigation: 2, requiredMilitary: 1,
    cost: { energy: 150, materials: { structural: 75, highTemp: 25, nuclear: 8 } }, buildYears: 12, hull: 100, propulsion: 75, sensors: 65, weapons: 70, cargoCapacity: 20, fuel: 130, speedMultiplier: 1,
  },
};

export function isTransportShipType(type: ShipType): boolean {
  return type === "transport" || type === "heavyTransport";
}

export function effectiveCruiseSpeedC(type: ShipType, navigationLevel: number): number {
  const level = Math.min(10, Math.max(1, navigationLevel));
  const baseSpeed = NAVIGATION_CRUISE_SPEED_C[level - 1] ?? NAVIGATION_CRUISE_SPEED_C[0]!;
  return baseSpeed * SHIP_TYPES[type].speedMultiplier;
}

/** Navigation improvements raise both transport holds without rewriting saved ships. */
export function effectiveCargoCapacity(ship: Pick<Ship, "type" | "cargoCapacity">, navigationLevel: number): number {
  if (!isTransportShipType(ship.type)) return ship.cargoCapacity;
  const level = Math.min(10, Math.max(1, navigationLevel));
  return Math.round(ship.cargoCapacity * (1 + (level - 1) * 0.2));
}

export const EMPTY_CARGO: Cargo = { materials: {} };
